// ToDo: .getInstance delegieren

import {mediator, timeoutRegistry} from "../mediator/mediatorInstance.js";

import { EventLinker } from "./controller/EventLinker.js";

import { DocumentMarkerManager } from "./marker/mainmap/DocumentMarkerManager.js";
import { ArchiveMarkerManager } from "./marker/mainmap/ArchiveMarkerManager.js";
import { MarkerClustererAdapter } from "./adapter/MarkerClustererAdapter.js";

import { GalleryMarkerManager } from "./marker/gallery/GalleryMarkerManager.js";
import {createMultiLocationControl} from "./tools/multiLocationControl.js";
import {MapCanvasManager} from "./tools/MapCanvasManager.js";
import {MapPositions} from "./tools/MapPositions.js";
import {MAP_OPTIONS} from "./tools/MapOptions.js";
import {MapFloatHelper} from "./tools/MapFloatHelper.js";
import {CircleSearch} from "./tools/CircleSearch.js";

export class Index {
    mapAdapter;
    eventLinker;
    mapCanvasManager;
    mapPositions;
    mapListeners;

    // marker
    markerFactory;
    markerClustererLib;
    markerClusterer;
    documentMarkerManager;
    archiveMarkerManager;
    galleryMarkerManager;

    // legacy bridge
    layoutConnector;
    globalObjectConnector;
    openGalleryLegacy;

    constructor({mapAdapter, markerFactory, markerClustererLib}) {
        this.mapAdapter = mapAdapter;
        this.mapCanvasManager = new MapCanvasManager("map_canvas", "galleryMapCanvas");
        this.mapPositions = new MapPositions();
        this.mapListeners = new Map();

        this.markerFactory = markerFactory;
        this.markerClustererLib = markerClustererLib;
        this.documentMarkerManager = new DocumentMarkerManager(
            mapAdapter, this.markerFactory
        );
        this.archiveMarkerManager = new ArchiveMarkerManager(
            mapAdapter, this.markerFactory
        );
        this.galleryMarkerManager = new GalleryMarkerManager(
            mapAdapter, this.markerFactory);

        this.mapFloatHelper = new MapFloatHelper(
            () => document.getElementById('left'),
            () => document.getElementById('topo_footer'),
            () => document.getElementById('left_wrapper'),
            { minHeight: 250, padding: 15 }
        );
    }

    #registerEvents() {
        this.eventLinker = new EventLinker(mediator, this.mapAdapter);
        this.eventLinker.registerEvents({
            onArchiveHover: this.highlightArchiveMarker.bind(this),
            onDocumentHover: this.highlightDocumentMarker.bind(this),
            onHoverShowThumbObject: this.globalObjectConnector.GetBasicJSHoverThumbObject.bind(this),
            onDocumentClick: this.openGalleryLegacy.bind(this),
            onMainMapFitToMarkers: this.fitToAllMainMapMarkers.bind(this),
            onGalleryMapFitToMarkers: this.fitToGalleryMapMarkers.bind(this),
        });
    }

    setOpenGalleryHandler(callback) {
        this.openGalleryLegacy = callback;
    }

    initializeLegacyBridges({ layoutConnector, globalObjectConnector }) {
        this.globalObjectConnector = globalObjectConnector;
        this.layoutConnector = layoutConnector;
    }

    registerEvents() {
        this.#registerEvents();
    }

    /**
     * Initialisiert die Karte (inkl. Übergabe von mapOptions an den Adapter)
     * @param {string} canvasId - DOM-Element-ID für die Karte
     * @param {object} mapOptions - Optionen wie center, zoom, mapTypeId usw.
     * @param {object} mode - mit welchen Optionspaket die Map initialisiert wird
     */
    initialize(canvasId, mapOptions = {}, mode = 'main') {
        const opts = { ...MAP_OPTIONS[mode], ...mapOptions };
        this.mapAdapter.initializeMap(canvasId, opts);

        this.circleSearch = new CircleSearch(this.mapAdapter, this.mapAdapter.getMapInstance(), mediator);
        this.circleSearch.setActive();
        this.markerClusterer = new MarkerClustererAdapter(this.markerClustererLib, this.mapAdapter.getMapInstance(), {
            render: this.documentMarkerManager.renderCustomClusterMarker
        });
        this.markerClusterer.initialize();
        this.documentMarkerManager.setMarkerClusterer(this.markerClusterer);

        this.addZoomControls();
    }

    setBaseMapOverlay(baseMapOverlayName) {
        this.mapAdapter.setWMTSMapOverlay(baseMapOverlayName);
    }

    setMapOptions(mode = 'main', customOpts = {}){
        const opts = { ...(MAP_OPTIONS[mode] || MAP_OPTIONS.main), ...customOpts };
        const map = this.mapAdapter.getMapInstance();
        if(map) {
            map.setOptions(opts);
        } else {
            console.warn('[TopoMapCore] No map instance available when seeting options.');
        }
    }

    setMapListeners() {
        this.mapListeners.set('heading_changed',
            this.mapAdapter.addListener(this.mapAdapter.getMapInstance(), 'heading_changed', () => {
                if (this.isMainActive()) {
                    this.documentMarkerManager.rotateMarkers();
                }
            })
        );
        this.mapListeners.set('projection_changed',
            this.mapAdapter.addListener(this.mapAdapter.getMapInstance(), 'projection_changed', () => {
                if (this.isMainActive()) {
                    this.documentMarkerManager.rotateMarkers();
                }
            })
        );
    }

    getMapInstance() {
        return this.mapAdapter.getMapInstance();
    }

    getCurrentCanvas() {
        const map = this.mapAdapter.getMapInstance();
        return map.getDiv();
    }

    hasValidMap() {
        return !!this.mapAdapter.getMapInstance();
    }

    isMainActive() {
        const map = this.mapAdapter.getMapInstance();
        return this.mapPositions.isMain() && !!map && document.body.contains(map.getDiv());
    }

    isGalleryActive() {
        const map = this.mapAdapter.getMapInstance();
        return this.mapPositions.isGallery() && !!map && document.body.contains(map.getDiv());
    }

    switchToGallery() {
        this.mapCanvasManager.switchToGallery();
        this.mapPositions.set(MapPositions.GALLERY);
    }

    restoreMain() {
        this.mapCanvasManager.restoreMain();
        this.mapPositions.set(MapPositions.MAIN);
    }

    addDocumentMarkers(dataArray) {
        this.documentMarkerManager.addMarkers(dataArray);
    }

    clearDocumentMarkers() {
        this.documentMarkerManager.clearMarkers();
    }

    highlightDocumentMarker(id, active = true) {
        this.documentMarkerManager.highlightMarkerContainingDocument(id, active);
    }

    addArchiveMarkers(dataArray) {
        this.archiveMarkerManager.addMarkers(dataArray);
    }

    clearArchiveMarkers() {
        this.archiveMarkerManager.clearMarkersByType('archive');
    }

    highlightArchiveMarker(id, active = true) {
        this.archiveMarkerManager.highlightMarkerById(id, active);
    }

    scheduleTimeout(name, delay, callback) {
        timeoutRegistry.schedule(name, delay, callback);
    }

    fitToAllMainMapMarkers(pan = false) {
        const allMarkers = [
            ...this.documentMarkerManager.getAllVisibleMarkers()
        ];

        const markersWithPosition = allMarkers.filter((marker) => marker.position);
        this.mapAdapter.fitMapToMarkers(this.mapAdapter.getMapInstance(), markersWithPosition, pan);
    }

    getAllDocumentMarkers() {
        return this.documentMarkerManager.getMarkersByType('document');
    }

    // Gallerie
    addGalleryMarkers({ locations, viewpoints, mapoptions }) {
        this.galleryMarkerManager.addMarkers(this.mapAdapter.getMapInstance(), { locations, viewpoints } );
    }

    deleteGalleryMarkers() {
        this.galleryMarkerManager.clearAll();
    }

    fitToGalleryMapMarkers(index = null) {
        this.mapAdapter.fitMapToMarkers(this.mapAdapter.getMapInstance(), this.galleryMarkerManager.getMarkers(index));
    }

    // Overlays
    createOverlay() {
        return this.mapAdapter.getOverlayInstance();
    }

    removeOverlay() {
        this.mapAdapter.removeOverlay();
    }

    listenOverlayReady(callback) {
        const overlay = this.mapAdapter.getOverlayInstance();
        if(overlay) {
            this.mapAdapter.addListener(overlay, 'ready', callback);
        }
    }

    // View-Dinge
    updateFloat() {
        if(this.isMainActive()) {
            this.mapFloatHelper.floatMainMap();
        } else if(this.isGalleryActive()) {
            this.mapFloatHelper.floatGalleryMap();
        }
    }

    createMultiLocationControl(allLabel) {
        const markers = this.galleryMarkerManager.getMarkers();
        if(!markers || markers.length <= 1) { return; }

        let index = 0;

        const container = createMultiLocationControl({
           onLeft: (labelEl) => {
               labelEl.textContent = `${index}/4`;
           },
            onRight: (labelEl) => {
               labelEl.textContent = `${index}/4`;
            },
            labelText: 'ALL'
        });
        const leftBtn = container.querySelector('button:first-of-type');
        const rightBtn = container.querySelector('button:last-of-type');
        const label = container.querySelector('span');

        const update = () => {
            const map = this.mapAdapter.getMapInstance();
            const markerSubset = this.galleryMarkerManager.getMarkers(index === 0 ? null : index - 1);
            this.mapAdapter.fitMapToMarkers(map, markerSubset);

            label.textContent = index === 0 ? allLabel : `${index} / ${markers.length}`;
        };
        leftBtn.addEventListener('click', () => {
            index = (index - 1 + markers.length +1) % (markers.length + 1);
            update();
        });
        rightBtn.addEventListener('click', () => {
            index = (index + 1 + markers.length +1) % (markers.length + 1);
            update();
        });

        this.mapAdapter.performMapAction('addControl', {
            element: container,
            position: 'BOTTOM_LEFT',
            type: 'multiLocation'
        });

        update();
    }

    addZoomControls(position = 'RIGHT_BOTTOM') {
        // Zoom out
        const zoomOutBtn = document.createElement('div');
        zoomOutBtn.className = 'zoom reduce';
        zoomOutBtn.title = 'Zoom out';
        zoomOutBtn.onclick = () => {
          this.mapAdapter.performMapAction('setZoom', { difference: -1 });
        };
        this.mapAdapter.performMapAction("addControl", {
            element: zoomOutBtn,
            position: position,
            type: 'zoom_out'
        });

        // Zoom in
        const zoomInBtn = document.createElement('div');
        zoomInBtn.className = 'zoom add';
        zoomInBtn.title = 'Zoom in';
        zoomInBtn.onclick = () => {
          this.mapAdapter.performMapAction('setZoom', { difference: 1 });
        };
        this.mapAdapter.performMapAction("addControl", {
            element: zoomInBtn,
            position: position,
            type: 'zoom_in'
        });
    }

    addArchiveToggleControl({ onToggle, labelText = "ARCHIVES_MORE", checked = false, css = {} } = {} ) {
        const container = document.createElement('div');
        container.className = 'topomarker-control';
        Object.assign(container.style, css);

        const label = document.createElement('label');
        label.setAttribute('for', 'cb_topomarker');
        label.textContent = labelText;

        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.id = 'cb_topomarker';
        checkbox.className = 'noshadow';
        checkbox.checked = checked;

        checkbox.addEventListener('click', ev => {
            if(typeof onToggle === 'function' ) { onToggle(ev.target.checked) }
        })

        container.appendChild(label);
        container.appendChild(checkbox);

        // ToDo: ent-hardcodisieren
        document.getElementById('TopoMarker').appendChild(container);

        return checkbox;
    }
}
